import { useCallback, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import AppButton from '../atoms/AppButton';
import { IconAlertTriangle, IconInfo, IconTrash } from '../atoms/Icon';

const TONES = {
  danger: { bg: '#fdecea', color: '#e74c3c', Icon: IconAlertTriangle },
  delete: { bg: '#fdecea', color: '#e74c3c', Icon: IconTrash },
  warning: { bg: 'var(--color-amber-ghost)', color: 'var(--color-amber-primary)', Icon: IconAlertTriangle },
  info: { bg: 'var(--color-purple-ghost)', color: 'var(--color-purple-primary)', Icon: IconInfo },
};

function ConfirmDialogModal({ state, onClose }) {
  if (!state) return null;
  const { title, message, isAlert, danger, confirmLabel, cancelLabel, resolve } = state;

  const toneKey = danger ? (isAlert ? 'danger' : 'delete') : (isAlert ? 'warning' : 'info');
  const tone = TONES[toneKey];
  const Icon = tone.Icon;

  function handleConfirm() {
    resolve(true);
    onClose();
  }

  function handleCancel() {
    resolve(false);
    onClose();
  }

  return (
    <Modal
      show
      onHide={handleCancel}
      centered
      size="sm"
      contentClassName="confirm-dialog-content"
      // Confirmar é sempre a camada mais alta: este diálogo é aberto de dentro
      // de modais (inclusive submodais), e com o z-index padrão do Bootstrap ele
      // nascia atrás deles — visível, mas impossível de clicar.
      className="confirm-dialog"
      backdropClassName="confirm-dialog-backdrop"
      // Sem isto o modal de trás puxa o foco de volta e some com o teclado.
      enforceFocus={false}
    >
      <button
        type="button"
        onClick={handleCancel}
        aria-label="Fechar"
        className="confirm-dialog-close"
      >
        ×
      </button>

      <Modal.Body className="text-center" style={{ padding: '36px 32px 24px' }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: tone.bg,
            color: tone.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 18px',
          }}
        >
          <Icon size={26} />
        </div>

        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-neutral)', marginBottom: 8 }}>
          {title}
        </div>
        <div style={{ fontSize: 14, color: '#888', lineHeight: 1.5 }}>
          {message}
        </div>
      </Modal.Body>

      <Modal.Footer
        className="justify-content-center"
        style={{ border: 'none', padding: '4px 32px 32px', gap: 10 }}
      >
        {!isAlert && (
          <AppButton variant="ghost" onClick={handleCancel} className="flex-grow-1">
            {cancelLabel}
          </AppButton>
        )}
        <AppButton
          variant={danger ? 'danger' : 'primary'}
          onClick={handleConfirm}
          className="flex-grow-1"
        >
          {isAlert ? 'OK' : confirmLabel}
        </AppButton>
      </Modal.Footer>
    </Modal>
  );
}

function useConfirmDialog() {
  const [state, setState] = useState(null);

  const confirm = useCallback((message, opts = {}) => (
    new Promise((resolve) => {
      setState({
        message,
        title: opts.title || 'Confirmar ação',
        confirmLabel: opts.confirmLabel || 'Confirmar',
        cancelLabel: opts.cancelLabel || 'Cancelar',
        danger: !!opts.danger,
        isAlert: false,
        resolve,
      });
    })
  ), []);

  const alertDialog = useCallback((message, opts = {}) => (
    new Promise((resolve) => {
      setState({
        message,
        title: opts.title || 'Aviso',
        danger: !!opts.danger,
        isAlert: true,
        resolve,
      });
    })
  ), []);

  const dialog = <ConfirmDialogModal state={state} onClose={() => setState(null)} />;

  return { confirm, alert: alertDialog, dialog };
}

export default useConfirmDialog;
